import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface WorkspaceTab {
  id: string; // unique tab ID
  entityType: string;
  entityId: string;
  title: string;
  openedAt: number; // timestamp
}

interface UIState {
  // Sidebar
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;
  toggleSidebar: () => void;

  // Workspace tabs (multi-WO)
  tabs: WorkspaceTab[];
  activeTabId: string | null;
  openTab: (tab: Omit<WorkspaceTab, 'openedAt'>) => void;
  closeTab: (id: string) => void;
  setActiveTab: (id: string | null) => void;

  // Tab scroll position memory (not persisted — ephemeral)
  tabScrollPositions: Record<string, number>;
  setScrollPosition: (tabId: string, y: number) => void;
  getScrollPosition: (tabId: string) => number;

  // Real-time connection status
  isOnline: boolean;
  isWebSocketConnected: boolean;
  setOnlineStatus: (online: boolean) => void;
  setWebSocketConnected: (connected: boolean) => void;

  // Pending sync indicator
  pendingSyncCount: number;
  setPendingSyncCount: (count: number) => void;
}

export const useUIStore = create<UIState>()(
  persist(
    (set, get) => ({
      sidebarCollapsed: false,
      setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),
      toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),

      tabs: [],
      activeTabId: null,
      openTab: (tab) => {
        const existing = get().tabs.find(
          (t) => t.entityType === tab.entityType && t.entityId === tab.entityId,
        );
        if (existing) {
          set({ activeTabId: existing.id });
          return;
        }
        const newTab: WorkspaceTab = { ...tab, openedAt: Date.now() };
        set((s) => ({ tabs: [...s.tabs, newTab], activeTabId: newTab.id }));
      },
      closeTab: (id) => {
        set((s) => {
          const tabs = s.tabs.filter((t) => t.id !== id);
          const activeTabId =
            s.activeTabId === id ? (tabs[tabs.length - 1]?.id ?? null) : s.activeTabId;
          const { [id]: _removed, ...remainingScrolls } = s.tabScrollPositions;
          return { tabs, activeTabId, tabScrollPositions: remainingScrolls };
        });
      },
      setActiveTab: (id) => set({ activeTabId: id }),

      tabScrollPositions: {},
      setScrollPosition: (tabId, y) =>
        set((s) => ({ tabScrollPositions: { ...s.tabScrollPositions, [tabId]: y } })),
      getScrollPosition: (tabId) => get().tabScrollPositions[tabId] ?? 0,

      isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
      isWebSocketConnected: false,
      setOnlineStatus: (online) => set({ isOnline: online }),
      setWebSocketConnected: (connected) => set({ isWebSocketConnected: connected }),

      pendingSyncCount: 0,
      setPendingSyncCount: (count) => set({ pendingSyncCount: count }),
    }),
    {
      name: 'tc-ui-state',
      partialize: (state) => ({
        sidebarCollapsed: state.sidebarCollapsed,
        tabs: state.tabs,
        activeTabId: state.activeTabId,
      }),
    },
  ),
);
